/**
 * GroupRidesEditor · v2.7.65+E238
 * ===============================
 * Website-Management editor for BOTH the home "Group Ride Rates" section and
 * the standalone Group Rides page. Fully AUTOSAVING (debounced PUT to
 * /admin/site/group-rides, no save button). Images upload (≤25 MB) via the
 * shared GridFS endpoint /admin/uploads/premium.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import ImageUploader from "@/components/admin/ImageUploader";
import adminApi from "@/lib/adminApi";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Check, CloudOff, Users, Image as ImageIcon, Link as LinkIcon, Phone, Mail } from "lucide-react";

const MAX_25 = 25 * 1024 * 1024;
const UPLOAD = "/admin/uploads/premium";

const newItem = () => ({ id: `gri_${Math.random().toString(36).slice(2, 10)}`, title: "", description: "", price: "", image_url: "", enabled: true });
const newPhoto = () => ({ id: `grp_${Math.random().toString(36).slice(2, 10)}`, image_url: "", caption: "" });

export default function GroupRidesEditor() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState("idle");
  const timer = useRef(null);

  useEffect(() => {
    api.get("/site/group-rides")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load Group Rides"))
      .finally(() => setLoading(false));
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, []);

  const scheduleSave = useCallback((next) => {
    setSaveState("saving");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try { await adminApi.put("/admin/site/group-rides", next); setSaveState("saved"); }
      catch { setSaveState("error"); toast.error("Autosave failed — retrying on next change"); }
    }, 700);
  }, []);

  const update = useCallback((fn) => {
    setData((cur) => {
      const next = JSON.parse(JSON.stringify(cur));
      fn(next);
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  if (loading || !data) {
    return <Card className="bg-zinc-900 border-zinc-800 p-10 text-center text-zinc-500" data-testid="group-editor-loading"><Loader2 className="h-7 w-7 animate-spin mx-auto" /></Card>;
  }

  const section = data.section || {};
  const page = data.page || {};
  const items = section.items || [];
  const photos = page.photos || [];

  return (
    <div className="space-y-4 mt-3" data-testid="group-editor">
      <Card className="bg-zinc-900 border-amber-400/30 p-5" data-testid="group-editor-header">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-lg font-black flex items-center gap-2 text-zinc-100"><Users className="h-5 w-5 text-amber-400" />Group Rides</h3>
            <p className="text-xs text-zinc-400 mt-1">Manages the home-page "Group Ride Rates" section and the standalone /group-rides page. Autosaves — no save button.</p>
          </div>
          <SaveBadge state={saveState} />
        </div>
      </Card>

      {/* ── HOME SECTION ────────────────────────────────── */}
      <Card className="bg-zinc-950 border border-zinc-800 p-4 space-y-4" data-testid="group-section-editor">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-widest text-amber-300 font-black">Home Section · Group Ride Rates</div>
          <label className="flex items-center gap-2 text-xs font-bold text-zinc-300" data-testid="group-section-enabled">
            <Switch checked={!!section.enabled} onCheckedChange={(v) => update((d) => { d.section.enabled = v; })} />
            {section.enabled ? "Visible" : "Hidden"}
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Labeled label="Section heading">
            <Input data-testid="group-section-heading" value={section.heading || ""} onChange={(e) => update((d) => { d.section.heading = e.target.value; })} className="bg-zinc-900 border-zinc-700 text-zinc-100" />
          </Labeled>
          <Labeled label="Subheading">
            <Input data-testid="group-section-subheading" value={section.subheading || ""} onChange={(e) => update((d) => { d.section.subheading = e.target.value; })} className="bg-zinc-900 border-zinc-700 text-zinc-100" />
          </Labeled>
        </div>
        <BgEditor obj={section} onMode={(m) => update((d) => { d.section.bg_mode = m; })} onColor={(c) => update((d) => { d.section.bg_color = c; })} onImage={(u) => update((d) => { d.section.bg_image_url = u; })} testid="group-section-bg" />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="group-items">
          {items.map((it, idx) => (
            <Card key={it.id} className="bg-zinc-900 border border-zinc-800 p-3 space-y-3" data-testid={`group-item-${idx}`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Rate #{idx + 1}</span>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1.5 text-[10px] text-zinc-400" data-testid={`group-item-${idx}-enabled`}>
                    <Switch checked={!!it.enabled} onCheckedChange={(v) => update((d) => { d.section.items[idx].enabled = v; })} />{it.enabled ? "Live" : "Hidden"}
                  </label>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-500 hover:text-rose-400" data-testid={`group-item-${idx}-delete`} onClick={() => update((d) => { d.section.items.splice(idx, 1); })}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
              <ImageUploader testid={`group-item-${idx}-photo`} label="Photo (≤25 MB)" value={it.image_url} onChange={(url) => update((d) => { d.section.items[idx].image_url = url; })} endpoint={UPLOAD} maxBytes={MAX_25} height={110} />
              <Input data-testid={`group-item-${idx}-title`} value={it.title || ""} onChange={(e) => update((d) => { d.section.items[idx].title = e.target.value; })} placeholder="Title" className="bg-zinc-950 border-zinc-700 text-zinc-100 font-bold" />
              <textarea data-testid={`group-item-${idx}-desc`} value={it.description || ""} onChange={(e) => update((d) => { d.section.items[idx].description = e.target.value; })} rows={2} placeholder="Info description" className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-100 resize-y" />
              <Input data-testid={`group-item-${idx}-price`} value={it.price || ""} onChange={(e) => update((d) => { d.section.items[idx].price = e.target.value; })} placeholder="Group price (e.g. 180.00)" inputMode="decimal" className="bg-zinc-950 border-zinc-700 text-zinc-100" />
              <div className="text-[9px] text-zinc-600">Learn More button links to the Group Rides page.</div>
            </Card>
          ))}
          {items.length === 0 && <div className="col-span-full text-center text-zinc-500 text-sm py-6 border border-dashed border-zinc-800 rounded-lg">No rates yet — add one below.</div>}
        </div>
        <Button data-testid="group-add-item" variant="outline" className="bg-zinc-950 border-amber-400/40 text-amber-300 hover:bg-amber-400/10" onClick={() => update((d) => { d.section.items = [...(d.section.items || []), newItem()]; })}>
          <Plus className="h-4 w-4 mr-1.5" />Add Rate
        </Button>
      </Card>

      {/* ── GROUP RIDES PAGE ────────────────────────────── */}
      <Card className="bg-zinc-950 border border-zinc-800 p-4 space-y-4" data-testid="group-page-editor">
        <div className="text-[11px] uppercase tracking-widest text-amber-300 font-black">Group Rides Page (/group-rides)</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Labeled label="Eyebrow (small label)"><Input data-testid="group-page-eyebrow" value={page.eyebrow || ""} onChange={(e) => update((d) => { d.page.eyebrow = e.target.value; })} className="bg-zinc-900 border-zinc-700 text-zinc-100" /></Labeled>
          <Labeled label="Header"><Input data-testid="group-page-header" value={page.header || ""} onChange={(e) => update((d) => { d.page.header = e.target.value; })} className="bg-zinc-900 border-zinc-700 text-zinc-100" /></Labeled>
        </div>
        <Labeled label="Descriptive text">
          <textarea data-testid="group-page-intro" value={page.intro || ""} onChange={(e) => update((d) => { d.page.intro = e.target.value; })} rows={4} className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-100 resize-y" />
        </Labeled>
        <BgEditor obj={page} onMode={(m) => update((d) => { d.page.bg_mode = m; })} onColor={(c) => update((d) => { d.page.bg_color = c; })} onImage={(u) => update((d) => { d.page.bg_image_url = u; })} testid="group-page-bg" />

        {/* Book Now + contact */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Labeled label="Book Now button label"><Input data-testid="group-page-book-label" value={page.book_now_label || ""} onChange={(e) => update((d) => { d.page.book_now_label = e.target.value; })} className="bg-zinc-900 border-zinc-700 text-zinc-100" /></Labeled>
          <Labeled label="Book Now link (URL or /path)"><div className="relative"><LinkIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" /><Input data-testid="group-page-book-link" value={page.book_now_link || ""} onChange={(e) => update((d) => { d.page.book_now_link = e.target.value; })} placeholder="/ride/login" className="bg-zinc-900 border-zinc-700 text-zinc-100 pl-7" /></div></Labeled>
          <Labeled label="Dispatch phone"><div className="relative"><Phone className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" /><Input data-testid="group-page-phone" value={page.dispatch_phone || ""} onChange={(e) => update((d) => { d.page.dispatch_phone = e.target.value; })} placeholder="(901) 459-9448" className="bg-zinc-900 border-zinc-700 text-zinc-100 pl-7" /></div></Labeled>
          <Labeled label="Dispatch email"><div className="relative"><Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" /><Input data-testid="group-page-email" value={page.dispatch_email || ""} onChange={(e) => update((d) => { d.page.dispatch_email = e.target.value; })} placeholder="support@bullertaxi.com" className="bg-zinc-900 border-zinc-700 text-zinc-100 pl-7" /></div></Labeled>
        </div>

        {/* Photos */}
        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-1"><ImageIcon className="h-3.5 w-3.5 text-amber-400" />Photos</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="group-photos">
            {photos.map((ph, idx) => (
              <Card key={ph.id} className="bg-zinc-900 border border-zinc-800 p-3 space-y-2" data-testid={`group-photo-${idx}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Photo #{idx + 1}</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-500 hover:text-rose-400" data-testid={`group-photo-${idx}-delete`} onClick={() => update((d) => { d.page.photos.splice(idx, 1); })}><Trash2 className="h-4 w-4" /></Button>
                </div>
                <ImageUploader testid={`group-photo-${idx}-img`} label="Photo (≤25 MB)" value={ph.image_url} onChange={(url) => update((d) => { d.page.photos[idx].image_url = url; })} endpoint={UPLOAD} maxBytes={MAX_25} height={110} />
                <Input data-testid={`group-photo-${idx}-caption`} value={ph.caption || ""} onChange={(e) => update((d) => { d.page.photos[idx].caption = e.target.value; })} placeholder="Caption (optional)" className="bg-zinc-950 border-zinc-700 text-zinc-100" />
              </Card>
            ))}
            {photos.length === 0 && <div className="col-span-full text-center text-zinc-500 text-sm py-6 border border-dashed border-zinc-800 rounded-lg">No photos yet.</div>}
          </div>
          <Button data-testid="group-add-photo" variant="outline" className="bg-zinc-950 border-amber-400/40 text-amber-300 hover:bg-amber-400/10" onClick={() => update((d) => { d.page.photos = [...(d.page.photos || []), newPhoto()]; })}>
            <Plus className="h-4 w-4 mr-1.5" />Add Photo
          </Button>
        </div>
      </Card>
    </div>
  );
}

function BgEditor({ obj, onMode, onColor, onImage, testid }) {
  return (
    <div className="space-y-2">
      <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Background</div>
      <div className="flex items-center gap-2 bg-zinc-900 rounded-md p-1 border border-zinc-800 w-fit" data-testid={`${testid}-mode`}>
        {["color", "image"].map((m) => (
          <button key={m} type="button" data-testid={`${testid}-mode-${m}`} onClick={() => onMode(m)}
            className={`px-3 py-1 rounded text-xs font-bold capitalize transition-colors ${obj.bg_mode === m ? "bg-amber-400 text-zinc-950" : "text-zinc-400 hover:text-zinc-200"}`}>{m}</button>
        ))}
      </div>
      {obj.bg_mode === "image" ? (
        <ImageUploader testid={`${testid}-image`} label="Background image (≤25 MB)" value={obj.bg_image_url} onChange={onImage} endpoint={UPLOAD} maxBytes={MAX_25} height={130} />
      ) : (
        <div className="flex items-center gap-3" data-testid={`${testid}-color`}>
          <input type="color" value={obj.bg_color || "#0b0b0c"} onChange={(e) => onColor(e.target.value)} className="h-10 w-16 rounded cursor-pointer bg-transparent border border-zinc-700" data-testid={`${testid}-color-picker`} />
          <Input value={obj.bg_color || ""} onChange={(e) => onColor(e.target.value)} placeholder="#0b0b0c" className="bg-zinc-900 border-zinc-700 text-zinc-100 w-40 font-mono" />
        </div>
      )}
    </div>
  );
}

function SaveBadge({ state }) {
  const map = {
    idle: { cls: "text-zinc-500", node: <>Autosaves</> },
    saving: { cls: "text-amber-300", node: <><Loader2 className="h-3 w-3 animate-spin" />Saving…</> },
    saved: { cls: "text-emerald-400", node: <><Check className="h-3 w-3" />Saved</> },
    error: { cls: "text-rose-400", node: <><CloudOff className="h-3 w-3" />Save failed</> },
  };
  const s = map[state] || map.idle;
  return <span className={`text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1 ${s.cls}`} data-testid="group-save-state">{s.node}</span>;
}

function Labeled({ label, children }) {
  return <label className="block"><div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">{label}</div>{children}</label>;
}
